import { IntegrationTypeCount } from '@mygpt/app/apis';
import { Http } from '@mygpt/app/http';

export const SECONDS_PER_DAY = 24 * 60 * 60;
export const SECONDS_PER_HOUR = 60 * 60;

export interface LarkFileListParams {
  name: string;
  sync_interval: number; // 单位天（最终会转换为秒，因为后端那边是秒）
  app_id: string;
  app_secret: string;
  share_urls: Array<{
    url: string;
    recursive: boolean;
  }>;
  notification_emails: string[];
}

export interface TaskStatusParams {
  task_id: string;
}

export interface LarkFile {
  created_time: string;
  modified_time: string;
  name: string;
  owner_id: string;
  parent_token: string;
  token: string;
  type: string;
  url: string;
}

interface PostLarkFileListResponse {
  message: string;
  rule_id: string;
}

/**
 * 将飞书文件列表提交到指定数据集
 */
export const postLarkFileList = (datasetId: string, data: LarkFileListParams) => {
  const { post } = Http.use();
  return post<PostLarkFileListResponse>(`/datasets/${datasetId}/integrations/lark`, {
    ...data,
    sync_interval: data.sync_interval * SECONDS_PER_DAY, // 从网页上的天单位转换为秒
  });
};

type IntegrationType = 'simple_url' | 'gitbook' | 'sitemap' | 'lark' | 'web_connector';

/**
 * 'waiting': 等待获取文件列表，任务尚未开始执行
 * 'syncing': 正在同步文件列表，任务已开始执行
 * 'failed': 获取文件列表失败，可能由于异常或其他原因，整个任务终止
 * 'completed': 文件列表同步已完成
 *
 * 注意：状态可能会自动变化，例如在自动更新时，系统会根据预定时间点自动切换状态
 */
type IntegrationStatus = 'waiting' | 'syncing' | 'failed' | 'completed';
interface IntegrationRuleDetails {
  id: string;
  created_at: string;
  updated_at: string;
  app_id: string;
  app_secret: string;
  share_urls: Array<{
    id: string;
    created_at: string;
    updated_at: string;
    deleted_at?: string;
    url: string;
    recursive: boolean;
  }>;
}

interface IntegrationCreator {
  user_id: string;
  email: string;
  name: string;
  salt: string | null;
  picture: string;
  locale: string | null;
  corporate: boolean;
  is_independent: boolean;
}

export interface IntegrationRule {
  id: string;
  file_count: number;
  created_at: string;
  updated_at: string;
  name: string;
  type: IntegrationType;
  file_list_sync_status: IntegrationStatus;
  created_by: IntegrationCreator | null;
  notification_emails: string | null;
  sync_interval: number;
  last_synced_at: string | null;
  detail: IntegrationRuleDetails;
  status?: IntegrationTypeCount;
  config: {
    enable_sync: boolean;
    interval_seconds: number;
    notification_emails: string[];
    // V2 schedule (后端 GET 时会输出)
    schedule?: {
      frequency: 'daily' | 'weekly' | 'monthly';
      day_of_week?: number;
      day_of_month?: number;
      hour: number;
      minute: number;
      timezone: string;
    };
    web_connector: {
      // V1 fields
      base_url: string;
      url_list: string[];
      max_pages: number;
      crawl_mode: 'recursive' | 'list';
      is_spa: boolean;
      // V2 fields (后端 GET 时会输出)
      url_patterns?: string[];
      exclude_patterns?: string[];
      discovery_mode?: 'auto' | 'sitemap' | 'crawler';
      crawl_from_exact_urls?: boolean;
    };
  };
}

export interface LarkIntegrationRulesResponse {
  items: IntegrationRule[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface GetLarkIntegrationsParams {
  type: Array<IntegrationType>;
  page?: number;
  size?: number;
  order_by?: string;
}

export const getLarkIntegrations = (datasetId: string, params: GetLarkIntegrationsParams) => {
  const { get } = Http.use();
  return get<LarkIntegrationRulesResponse>(`/datasets/${datasetId}/integrations`, { params });
};

export interface UpdateIntegrationRuledata {
  name: string; // 字符串类型，表示集成规则名称
  sync_interval: number; // 整数类型，表示同步间隔，单位为小时，最终会转换为秒
}

export const updateIntegrationRule = (
  datasetId: string,
  integrationRuleId: string,
  data: UpdateIntegrationRuledata
) => {
  const { patch } = Http.use();

  return patch(`/datasets/${datasetId}/integrations/${integrationRuleId}`, {
    name: data.name,
    sync_interval: data.sync_interval,
  });
};

export const syncIntegrationRule = (datasetId: string, integrationRuleId: string) => {
  const { post } = Http.use();
  return post(`/datasets/${datasetId}/integrations/${integrationRuleId}/sync`);
};

export const deleteIntegrationRule = (datasetId: string, integrationRuleId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/datasets/${datasetId}/integrations/${integrationRuleId}`);
};
